// src/components/ui/FilterDropdown.js  (o src/utils/filterDropdown.js)
import React, { useState, useMemo, useRef, useEffect } from 'react';

const normalizeOptions = (options = []) =>
  options.map(opt =>
    typeof opt === 'string'
      ? { value: opt, label: opt }
      : opt
  );

const FilterDropdown = ({
  label,
  value,
  options = [],
  onChange,
  placeholder = 'Todos',
  disabled = false,
  withSearch = true,
  className = '',
  isOpen = false,
  onOpenChange,
  multiple = false,
}) => {
  const containerRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');

  const normalized = useMemo(
    () => normalizeOptions(options),
    [options]
  );

  const filteredOptions = useMemo(() => {
    const q = searchTerm.toLowerCase();
    if (!withSearch || !q) return normalized;
    return normalized.filter(o =>
      o.label.toLowerCase().includes(q)
    );
  }, [normalized, searchTerm, withSearch]);

  // Calcular texto del botón
  const buttonText = useMemo(() => {
    if (!value || (Array.isArray(value) && value.length === 0)) return placeholder;

    if (multiple) {
      const count = Array.isArray(value) ? value.length : 0;
      return count > 0 ? `${count} seleccionados` : placeholder;
    }

    return normalized.find(o => o.value === value)?.label || placeholder;
  }, [value, normalized, placeholder, multiple]);

  const handleSelect = (val) => {
    if (disabled) return;

    if (multiple) {
      const currentSelected = Array.isArray(value) ? value : [];
      const isSelected = currentSelected.includes(val);

      let newSelected;
      if (isSelected) {
        newSelected = currentSelected.filter(v => v !== val);
      } else {
        newSelected = [...currentSelected, val];
      }

      if (onChange) onChange(newSelected);
    } else {
      if (onChange) onChange(val);
      if (onOpenChange) onOpenChange(false);
      setSearchTerm('');
    }
  };

  const toggleOpen = () => {
    if (disabled || !normalized.length) return;
    if (onOpenChange) {
      onOpenChange(!isOpen);
      if (!isOpen) {
        setSearchTerm('');
      }
    }
  };

  const renderOption = (option) => {
    const isSelected = multiple
      ? Array.isArray(value) && value.includes(option.value)
      : value === option.value;

    return (
      <button
        key={option.value}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          handleSelect(option.value);
        }}
        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2 cursor-pointer ${
          isSelected ? 'bg-pink-50 text-pink-700 font-medium' : 'text-gray-700'
        }`}
        style={{ pointerEvents: 'auto' }}
      >
        {multiple && (
          <input
            type="checkbox"
            checked={isSelected}
            readOnly
            className="rounded border-gray-300 text-pink-600 focus:ring-pink-500 pointer-events-none"
          />
        )}
        <span className="truncate">{option.label}</span>
        {!multiple && isSelected && (
          <span className="ml-auto">✓</span>
        )}
      </button>
    );
  };

  useEffect(() => {
      if (!isOpen) return;

      // IMPORTANTE: Usar setTimeout para evitar que el click que abre el dropdown lo cierre inmediatamente
      const timeoutId = setTimeout(() => {
        const handleClickOutside = (event) => {
          if (
            containerRef.current &&
            !containerRef.current.contains(event.target)
          ) {
            if (onOpenChange) onOpenChange(false);
            setSearchTerm('');
          }
        };

      document.addEventListener('click', handleClickOutside);
      
      // Guardar la función para poder removerla después
      containerRef.current._clickHandler = handleClickOutside;
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      if (containerRef.current?._clickHandler) {
        document.removeEventListener('click', containerRef.current._clickHandler);
        delete containerRef.current._clickHandler;
      }
    };
  }, [isOpen, onOpenChange]);

  return (
    <div ref={containerRef} className={`flex flex-col gap-1 ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}

      <div className="relative">
        <button 
          type="button"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm flex items-center justify-between gap-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
          onClick={toggleOpen}
          disabled={disabled}
        >
          <span className={`truncate ${disabled ? 'text-gray-400' : 'text-gray-900'}`}>
          {buttonText}
          </span>
          <svg
            className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              d="M5 7.5L10 12.5L15 7.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* Menú desplegable */}
        {isOpen && !disabled && normalized.length > 0 && (
          <div className="absolute z-[9999] mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
            {/* Buscador opcional */}
            {withSearch && (
              <div className="p-2 border-b border-gray-200 bg-white sticky top-0 z-10">
                <input
                  type="text"
                  placeholder={`Buscar ${label?.toLowerCase() || ''}...`}
                  className="w-full px-2 py-1 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  autoFocus
                />
              </div>
            )}

            <div className="py-1">
              {filteredOptions.length === 0 && (
                <div className="px-3 py-2 text-xs text-gray-400">
                  Sin resultados
                </div>
              )}

              {filteredOptions.map(renderOption)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterDropdown;